import React from 'react';
import { Icon } from '../../../components/elements';
import styled from '../../../components/styled';
import * as Colors from './../../../helpers/Colors';

import {
  SnippetDataDictionary,
  SnippetType,
  StandingOrderStatus,
  TaskStatus,
} from '../../../services/dataModels';

interface ItemProps {
  value: string;
  onPress?: () => void;
}

const Status = ({ value, onPress }: ItemProps) => {
  return (
    <StatusContainer onPress={onPress} disabled={!onPress}>
      <StatusTitle>
        <StatusValue>{value}</StatusValue>
        {onPress && <StatusActionIcon name="edit" color={Colors.secondary} />}
      </StatusTitle>
      <StatusLabel>Status</StatusLabel>
    </StatusContainer>
  );
};

const StatusContainer = styled.TouchableOpacity``;

const StatusTitle = styled.View`
  flex-direction: row;
  align-items: center;
`;

const StatusValue = styled.Text`
  font-size: 28px;
`;

const StatusLabel = styled.Text`
  opacity: 0.5;
`;

const StatusActionIcon = styled(Icon)`
  margin-left: 4px;
`;

const Percentage = ({ value, onPress }: ItemProps) => {
  return (
    <PercentageContainer onPress={onPress} disabled={!onPress}>
      <PercentageValue>{value ? `${value}%` : 'Task %'}</PercentageValue>
      {onPress && <PercentageIcon name="arrow-right" color="white" />}
    </PercentageContainer>
  );
};

const PercentageContainer = styled.TouchableOpacity<{ onPress: () => void }>`
  background: ${(props) => (props.onPress ? Colors.secondary : Colors.disabledButton)};
  padding: ${(props) => (props.onPress ? '5px 10px 5px 20px' : '8px 20px')};
  border-radius: 5px;
  flex-direction: row;
  align-items: center;
`;

const PercentageValue = styled.Text`
  font-size: 15px;
  color: white;
  font-weight: bold;
  text-align: center;
`;

const PercentageIcon = styled(Icon)``;

interface HeaderProps {
  snippetType: SnippetType;
  row: SnippetDataDictionary;
  onStatusPressed: () => void;
  onPercentagePressed: () => void;
  readOnly?: boolean;
}

const Header = ({
  snippetType,
  row,
  onStatusPressed,
  onPercentagePressed,
  readOnly = false,
}: HeaderProps) => {
  switch (snippetType) {
    case 'StandingOrder':
      switch (row.statusDisplayName as StandingOrderStatus) {
        case 'Completed':
          return (
            <Container>
              <Status value={row.statusDisplayName as string} />
            </Container>
          );
        default:
          return (
            <Container>
              <Status
                value={row.statusDisplayName as string}
                onPress={readOnly ? undefined : onStatusPressed}
              />
            </Container>
          );
      }
    case 'Task':
      switch (row.state as TaskStatus) {
        case 'Planned':
          return (
            <Container>
              <Status
                value={row.state as string}
                onPress={readOnly ? undefined : onStatusPressed}
              />
              {row.completionPercent && <Percentage value={row.completionPercent as string} />}
            </Container>
          );
        case 'In-Progress':
          return (
            <Container>
              <Status
                value={row.state as string}
                onPress={readOnly ? undefined : onStatusPressed}
              />
              <Percentage
                value={row.completionPercent as string}
                onPress={readOnly ? undefined : onPercentagePressed}
              />
            </Container>
          );
        case 'Completed':
        case 'Cancelled':
          return (
            <Container>
              <Status value={row.state as string} />
              {row.completionPercent && <Percentage value={row.completionPercent as string} />}
            </Container>
          );
        default:
          return (
            <Container>
              <Status
                value={row.state as string}
                onPress={readOnly ? undefined : onStatusPressed}
              />
              <Percentage value={row.completionPercent as string} />
            </Container>
          );
      }
    default:
      return <></>;
  }
};

const Container = styled.View`
  background: white;
  padding: 20px;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

export default Header;
